'use client';

import { useState, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Check, Minus, Zap } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { TIERS, getTierByName } from "@workspace/tier-config";

const paidTiers = TIERS.filter((t) => t.key !== "free" && t.key !== "enterprise");
const freeTier = TIERS.find((t) => t.key === "free")!;

function fmtLimit(n: number): string {
  return n === Infinity ? "Unlimited" : n.toLocaleString();
}

const comparisonRows = [
  {
    feature: "Content items",
    free: fmtLimit(getTierByName("free").maxPages),
    lite: fmtLimit(getTierByName("lite").maxPages),
    starter: fmtLimit(getTierByName("starter").maxPages),
    pro: fmtLimit(getTierByName("pro").maxPages),
    business: fmtLimit(getTierByName("business").maxPages),
    scale: fmtLimit(getTierByName("scale").maxPages),
    enterprise: fmtLimit(getTierByName("enterprise").maxPages),
  },
  {
    feature: "Sites",
    free: fmtLimit(getTierByName("free").maxSites),
    lite: fmtLimit(getTierByName("lite").maxSites),
    starter: fmtLimit(getTierByName("starter").maxSites),
    pro: fmtLimit(getTierByName("pro").maxSites),
    business: fmtLimit(getTierByName("business").maxSites),
    scale: fmtLimit(getTierByName("scale").maxSites),
    enterprise: fmtLimit(getTierByName("enterprise").maxSites),
  },
  { feature: "All schema types", free: true, lite: true, starter: true, pro: true, business: true, scale: true, enterprise: true },
  { feature: "Webflow integration", free: false, lite: false, starter: true, pro: true, business: true, scale: true, enterprise: true },
  { feature: "Auto-generated llms.txt", free: true, lite: true, starter: true, pro: true, business: true, scale: true, enterprise: true },
  { feature: "Content permissions", free: true, lite: true, starter: true, pro: true, business: true, scale: true, enterprise: true },
  { feature: "Auto-sync on publish", free: false, lite: false, starter: true, pro: true, business: true, scale: true, enterprise: true },
  { feature: "Analytics dashboard", free: false, lite: "Basic", starter: true, pro: true, business: true, scale: true, enterprise: true },
  { feature: "Webhook cache invalidation", free: false, lite: false, starter: false, pro: true, business: true, scale: true, enterprise: true },
  { feature: "API access", free: false, lite: false, starter: false, pro: true, business: true, scale: true, enterprise: true },
  { feature: "White-label endpoints", free: false, lite: false, starter: false, pro: false, business: true, scale: true, enterprise: true },
  { feature: "Multi-region caching", free: false, lite: false, starter: false, pro: false, business: false, scale: true, enterprise: true },
  { feature: "SLA uptime", free: "—", lite: "—", starter: "—", pro: "—", business: "99.9%", scale: "99.95%", enterprise: "Custom" },
  { feature: "Support", free: "Community", lite: "Email", starter: "Email", pro: "Priority email", business: "Dedicated Slack", scale: "Slack + QBR", enterprise: "Dedicated CSM" },
  { feature: "Free trial", free: "—", lite: "21 days", starter: "21 days", pro: "30 days", business: "21 days", scale: "—", enterprise: "—" },
  { feature: "SSO", free: false, lite: false, starter: false, pro: false, business: false, scale: false, enterprise: true },
  { feature: "Procurement support", free: false, lite: false, starter: false, pro: false, business: false, scale: false, enterprise: true },
];

type CellVal = boolean | string;

function CellValue({ val }: { val: CellVal }) {
  if (val === true) return <Check className="w-4 h-4 text-primary mx-auto" />;
  if (val === false) return <Minus className="w-4 h-4 text-border mx-auto" />;
  return <span className="text-sm text-foreground font-medium">{val}</span>;
}

function PricingClientInner() {
  const [annual, setAnnual] = useState(false);
  const router = useRouter();
  const params = useSearchParams();
  const { user } = useAuth({ skipRedirect: true });

  const fromDashboard = params?.get("from") === "dashboard";

  function getTierHref(tierKey: string): string {
    const isContactSales = tierKey === "scale" || tierKey === "enterprise";
    if (isContactSales) return `/contact?plan=${tierKey}`;

    if (user && fromDashboard) {
      const billingParam = annual ? "annual" : "monthly";
      return `/checkout?tier=${tierKey}&billing=${billingParam}`;
    }

    if (user) {
      return `/onboarding?plan=${tierKey}`;
    }

    if (tierKey === "free") return `/signup?plan=free`;
    return `/signup?plan=${tierKey}&trial=true`;
  }

  function handleTierCTA(tierKey: string) {
    router.push(getTierHref(tierKey));
  }

  return (
    <>
      {/* BILLING TOGGLE */}
      <div className="flex items-center justify-center gap-4 mb-12">
        <span className={`text-sm font-medium ${!annual ? "text-foreground" : "text-muted-foreground"}`}>Monthly</span>
        <button
          onClick={() => setAnnual(!annual)}
          className={`relative w-12 h-6 rounded-full transition-colors focus:outline-none ${annual ? "bg-primary" : "bg-border"}`}
          aria-label="Toggle annual billing"
        >
          <span
            className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${annual ? "translate-x-6" : "translate-x-0"}`}
          />
        </button>
        <span className={`text-sm font-medium ${annual ? "text-foreground" : "text-muted-foreground"}`}>
          Annual
          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">Save 30%</span>
        </span>
      </div>

      {/* FREE TIER BANNER */}
      <div className="mb-8 rounded-2xl border border-border bg-gray-50 px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-lg font-display font-bold text-foreground">{freeTier.name}</span>
            <span className="text-2xl font-display font-extrabold text-foreground">${freeTier.price.monthly}</span>
            <span className="text-sm text-muted-foreground">forever</span>
          </div>
          <p className="text-sm text-muted-foreground">{freeTier.maxPages} content items · {freeTier.maxSites} site · Community support · All core schema types</p>
          <p className="text-xs text-muted-foreground/70 mt-1">For personal blogs and testing. Not for commercial use.</p>
        </div>
        <Link
          href={getTierHref("free")}
          className="shrink-0 px-6 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-white transition-colors"
        >
          {fromDashboard ? "Switch to Free" : "Get started free →"}
        </Link>
      </div>

      {/* PAID TIERS — horizontal scroll so all 5 fit cleanly */}
      <div className="overflow-x-auto pb-2 mb-8">
        <div className="flex gap-4 min-w-max mx-auto justify-center">
          {paidTiers.map((tier) => (
            <div
              key={tier.key}
              className={`relative rounded-2xl flex flex-col w-52 shrink-0 ${
                tier.featured
                  ? "bg-primary text-primary-foreground shadow-2xl shadow-primary/20 ring-2 ring-primary scale-[1.02]"
                  : "bg-white border border-border"
              }`}
            >
              {tier.badge && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full shadow-lg">
                    <Zap className="w-3 h-3" /> {tier.badge}
                  </span>
                </div>
              )}

              <div className="p-6 flex-1 flex flex-col">
                <p className={`text-xs font-bold tracking-widest uppercase mb-3 ${tier.featured ? "text-blue-200" : "text-muted-foreground"}`}>
                  {tier.name}
                </p>

                <div className="mb-4">
                  <div className="flex items-end gap-1">
                    <span className={`text-3xl font-display font-extrabold ${tier.featured ? "text-white" : "text-foreground"}`}>
                      ${annual ? tier.price.annual : tier.price.monthly}
                    </span>
                    <span className={`text-sm mb-1 ${tier.featured ? "text-blue-200" : "text-muted-foreground"}`}>/mo</span>
                    {annual && (
                      <span className={`text-sm mb-1 ml-1 line-through ${tier.featured ? "text-blue-300/60" : "text-muted-foreground/50"}`}>
                        ${tier.price.monthly}
                      </span>
                    )}
                  </div>
                  {annual && tier.price.annualYearly != null && (
                    <p className={`text-xs mt-0.5 ${tier.featured ? "text-blue-200" : "text-muted-foreground"}`}>
                      ${tier.price.annualYearly?.toLocaleString()}/yr
                    </p>
                  )}
                  {tier.trialDays > 0 && (
                    <p className={`text-xs mt-1 ${tier.featured ? "text-blue-200" : "text-green-600 font-medium"}`}>
                      {tier.trialDays}-day free trial
                    </p>
                  )}
                </div>

                {tier.persona && (
                  <p className={`text-xs mb-4 leading-relaxed ${tier.featured ? "text-blue-200" : "text-muted-foreground"}`}>
                    {tier.persona}
                  </p>
                )}

                <ul className="space-y-2.5 flex-1 mb-6">
                  {tier.features.slice(0, 5).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs">
                      <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tier.featured ? "text-blue-200" : "text-primary"}`} />
                      <span className={tier.featured ? "text-blue-50" : "text-foreground"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleTierCTA(tier.key)}
                  className={`w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer ${
                    tier.featured
                      ? "bg-white text-primary hover:bg-blue-50"
                      : "bg-primary text-primary-foreground hover:bg-primary/90"
                  }`}
                >
                  {tier.contactSales ? "Contact sales" : tier.trialDays > 0 ? `Start ${tier.trialDays}-day trial` : tier.ctaLabel}
                </button>

                <p className={`text-center text-xs mt-2 ${tier.featured ? "text-blue-200/50" : "text-muted-foreground/50"}`}>
                  {tier.trialDays > 0 ? "Cancel anytime during trial" : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ENTERPRISE BANNER */}
      <div className="hash-bg-dark rounded-2xl px-8 py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
        <div>
          <p className="text-xs font-bold tracking-widest text-blue-400 uppercase mb-2">Enterprise</p>
          <p className="text-2xl font-display font-bold text-white mb-1">Custom pricing — starting at $1,500/mo</p>
          <p className="text-blue-200/70 text-sm max-w-lg">
            Unlimited items · Unlimited sites · Custom SLA · Dedicated CSM · White-glove onboarding · SSO · Procurement &amp; legal support
          </p>
        </div>
        <Link
          href="/contact?plan=enterprise"
          className="shrink-0 px-8 py-3.5 rounded-xl bg-white text-foreground font-semibold text-sm hover:bg-blue-50 transition-colors"
        >
          Contact sales →
        </Link>
      </div>

      {/* COMPARISON TABLE */}
      <div className="mt-24">
        <p className="text-xs font-bold tracking-widest text-primary uppercase mb-4">Compare plans</p>
        <h2 className="text-3xl font-display font-bold text-foreground mb-12">
          <span className="text-primary">#</span> Compare every plan
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-4 pr-4 font-semibold text-muted-foreground w-44">Feature</th>
                {["Free", "Lite", "Starter", "Pro", "Business", "Scale", "Enterprise"].map((h) => (
                  <th key={h} className={`text-center py-4 px-2 font-bold text-sm ${h === "Pro" ? "text-primary" : "text-foreground"}`}>
                    {h === "Pro" ? (
                      <><span>Pro</span><span className="block text-xs font-normal text-primary">Most popular</span></>
                    ) : h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {comparisonRows.map((row) => (
                <tr key={row.feature} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 pr-4 text-muted-foreground font-medium text-xs">{row.feature}</td>
                  <td className="py-3 px-2 text-center"><CellValue val={row.free} /></td>
                  <td className="py-3 px-2 text-center"><CellValue val={row.lite} /></td>
                  <td className="py-3 px-2 text-center"><CellValue val={row.starter} /></td>
                  <td className="py-3 px-2 text-center bg-primary/5"><CellValue val={row.pro} /></td>
                  <td className="py-3 px-2 text-center"><CellValue val={row.business} /></td>
                  <td className="py-3 px-2 text-center"><CellValue val={row.scale} /></td>
                  <td className="py-3 px-2 text-center"><CellValue val={row.enterprise} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export function PricingClient() {
  return (
    <Suspense fallback={null}>
      <PricingClientInner />
    </Suspense>
  );
}
